import { Box, Flex, Image, List, Text, useColorMode } from "@chakra-ui/react";
import { lazy, Suspense, useEffect } from "react";
import ProductSkeletonDesctop from "../../skeletons/Products/ProductSkeletonDesktop";
import ProductSkeletonMobile from "../../skeletons/Products/ProductSkeletonMobile";
import Pagination from "../Pagination/Pagination";
import PriceHistory from "../PriceHistory/PriceHistory";
import CaliberPriceHistory from "../CaliberPriceHistory/CaliberPriceHistory";
import { useLocation, useSearchParams } from "react-router-dom";
import useSearchParamsHandler from "../../hooks/useSearchParamsHandler";
import Newsletter from "../Newsletter/Newsletter";
import { cleanCustomHTML } from "../../utils/cleanCustomHTML";
import { getCurrentColor } from "../../constants";

const ProductItem = lazy(() => import("../ProductItem/ProductItem"));
const SelectedFiltersSticky = lazy(
  () => import("../Filters/SelectedFiltersSticky")
);

const ProductList = ({
  slider,
  currentPage,
  allPages,
  productsInfo,
  productsLoadingStatus,
  locationState,
  customHTML,
}) => {
  const { colorMode } = useColorMode();

  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const { params } = useSearchParamsHandler();

  const fullUrl = location.pathname + location.search;

  if (!slider) {
    localStorage.setItem("fullUrl", fullUrl);
  }

  useEffect(() => {
    if (!slider) {
      const sfParam = params?.sf;
      const sfArray = typeof sfParam === "string" ? sfParam.split(",") : [];
      const sfSet = new Set(sfArray);

      if (sfSet.size === 0) {
        sfSet.add("1");
        sfSet.add("2");
      }

      const newParams = new URLSearchParams(params);
      newParams.set("sf", Array.from(sfSet).join(","));

      setSearchParams(newParams, { replace: true });
    }
  }, [location.pathname, setSearchParams, slider, params]);

  const renderProductsSkeleton = () => (
    <Flex
      flexDirection={{ base: "row", xl: "column" }}
      flexWrap={"wrap"}
      gap={"3px"}
      justifyContent={"space-around"}
    >
      {[...Array(30)].map((item, index) =>
        window.screen.width > 768 ? (
          <ProductSkeletonDesctop key={index} />
        ) : (
          <ProductSkeletonMobile key={index} />
        )
      )}
    </Flex>
  );

  const renderNotFound = (
    <Flex flexDirection={"column"} alignItems={"center"}>
      <Text>No products were found for these filters</Text>
      <Image
        alt="Not found"
        w={"70%"}
        src={
          colorMode === "light"
            ? "https://cdn.bulletscout.com/image/not-found.png"
            : "https://cdn.bulletscout.com/image/not-found-dark1.png"
        }
      />
    </Flex>
  );

  const cleanedHTML = cleanCustomHTML(customHTML);

  const backgroundColor = getCurrentColor(
    colorMode,
    "whiteC.100",
    "grayC.1700"
  );
  const textColor = getCurrentColor(colorMode, "blackC.100", "whiteC.100");
  const borderColor = getCurrentColor(colorMode, "grayC.800", "grayC.2400");

  return (
    <Flex flexDirection={"column"} w={"100%"} alignItems={"center"}>
      {productsLoadingStatus === "loading" ? renderProductsSkeleton() : null}
      {productsLoadingStatus !== "loading" &&
        productsInfo &&
        productsInfo.length <= 0 &&
        renderNotFound}
      <Suspense fallback={renderProductsSkeleton()}>
        <Newsletter isPopup={true} />

        <List
          mb={{ base: "58px", md: "52px" }}
          maxWidth={"1015px"}
          w={"100%"}
          display={"flex"}
          flexDirection={{ base: "column", lg: "row", xl: "column" }}
          justifyContent={{ base: "space-around", xl: "flex-start" }}
          flexWrap={"wrap"}
          gap={3}
          ml={"auto"}
        >
          {productsInfo &&
            productsInfo.map((product, i) => (
              <ProductItem key={i} product={product} slider={slider} />
            ))}
        </List>
        <SelectedFiltersSticky />
      </Suspense>
      {productsInfo &&
        Object.values(productsInfo).length > 0 &&
        currentPage &&
        allPages && <Pagination slider={slider} />}
      {slider && <PriceHistory locationState={locationState} />}
      {!slider && <CaliberPriceHistory />}
      {customHTML && (
        <Box
          mt={4}
          bg={backgroundColor}
          color={textColor}
          border="1px solid"
          borderColor={borderColor}
          p="20px"
          textAlign="center"
          fontSize="1.1em"
          fontFamily="Arial, sans-serif"
          dangerouslySetInnerHTML={{ __html: cleanedHTML }}
        />
      )}
    </Flex>
  );
};

export default ProductList;
